import { liteClient } from "algoliasearch/lite";
import _ from "lodash";
import {
  ALGOLIA_APPLICATION_ID,
  ALGOLIA_AUTHORITY_INDEX,
  ALGOLIA_COURT_INDEX,
  ALGOLIA_ORGANIZATION_INDEX,
  ALGOLIA_PROSECUTION_INDEX,
  ALGOLIA_SEARCH_API_KEY,
} from "../../config/_entrypoint";
import { DatasetConfig } from "./datasetTypes";
import {
  contractualPartnerFields,
  debtCollectionAgencyFields,
  debtCollectionAgencyFieldsReduced,
  opposingCarLiabilityInsuranceFields,
  opposingLiabilityInsuranceFields,
} from "../../services/Product/FormDefinitions/modules/organizationFields";
import OrganizationHit from "../Case/CaseEntityPicker/Hit/OrganizationHit";
import { authorityFields } from "../../services/Product/FormDefinitions/modules/authorityFields";
import AuthorityHit from "../Case/CaseEntityPicker/Hit/AuthorityHit";
import { prosecutionFields } from "../../services/Product/FormDefinitions/modules/prosecutionFields";
import ProsecutionHit from "../Case/CaseEntityPicker/Hit/ProsecutionHit";
import CourtHit from "../CourtPicker/CourtHit";
import { courtFields } from "../../services/Product/FormDefinitions/modules/courtFields";
import { OrganizationEmail } from "../../types/OrganizationEmail";
import { userHasOneOfTheseRoles } from "../../services/backofficeUserService.ts";
import { Roles } from "../../types/BackofficeUser.ts";

export const datasetConfigs: { [x: string]: DatasetConfig } = {
  contractualPartnerLegalEntity: {
    entityApiData: {
      entityApiUri: "organizations",
      baseEntityAttribute: "contractualPartnerLegalEntity",
      additionalSaveData: {
        isChecked: true,
        type: "contractualPartnerLegalEntity",
      },
      additionalBaseEntityData: {
        opposingPartyType: "opposingPartyLegalEntity",
      },
    },
    searchConfig: {
      indexName: ALGOLIA_ORGANIZATION_INDEX,
      requestOptions: { filters: "type:contractualPartnerLegalEntity" },
      idPrefix: "organization",
    },
    resultListConfig: {
      itemSize: 150,
    },
    entityFormElements: contractualPartnerFields({
      path: "",
      translationPrefix: "contractualPartnerLegalEntity",
    }),
    HitComponent: OrganizationHit,
  },
  debtCollectionAgency: {
    entityApiData: {
      entityApiUri: "organizations",
      baseEntityAttribute: "debtCollectionAgency",
      additionalSaveData: {
        isChecked: true,
        type: "debtCollectionAgency",
      },
      additionalBaseEntityData: {},
    },
    searchConfig: {
      indexName: ALGOLIA_ORGANIZATION_INDEX,
      requestOptions: { filters: "type:debtCollectionAgency" },
      idPrefix: "organization",
    },
    formFieldTransformations: {
      additionalEmails: (value: OrganizationEmail[]) =>
        value.map((organizationEmail) => organizationEmail["@id"] ?? organizationEmail),
    },
    resultListConfig: {
      itemSize: 150,
    },
    entityFormElements: debtCollectionAgencyFields({
      path: "",
      translationPrefix: "debtCollectionAgency",
    }),
    HitComponent: OrganizationHit,
  },
  authority: {
    entityApiData: {
      entityApiUri: "authorities",
      baseEntityAttribute: "responsibleAuthority",
      additionalSaveData: {},
      additionalBaseEntityData: {},
    },
    searchConfig: {
      indexName: ALGOLIA_AUTHORITY_INDEX,
      idPrefix: "authority",
    },
    resultListConfig: {
      itemSize: 150,
    },
    entityFormElements: authorityFields({ path: "", translationPrefix: "authority" }),
    HitComponent: AuthorityHit,
  },
  prosecution: {
    entityApiData: {
      entityApiUri: "prosecutions",
      baseEntityAttribute: "responsibleProsecution",
      additionalSaveData: {},
      additionalBaseEntityData: {},
    },
    searchConfig: {
      indexName: ALGOLIA_PROSECUTION_INDEX,
      idPrefix: "prosecution",
    },
    resultListConfig: {
      itemSize: 150,
    },
    entityFormElements: prosecutionFields({ path: "", translationPrefix: "prosecution" }),
    HitComponent: ProsecutionHit,
  },
  court: {
    entityApiData: {
      entityApiUri: "courts",
      baseEntityAttribute: "responsibleCourt",
      additionalSaveData: {},
      additionalBaseEntityData: {},
    },
    searchConfig: {
      indexName: ALGOLIA_COURT_INDEX,
      idPrefix: "court",
    },
    resultListConfig: {
      itemSize: 200,
    },
    formFieldTransformations: {
      advoAssistId: (value) => _.toNumber(value),
    },
    entityFormElements: courtFields({ path: "", translationPrefix: "court" }),
    HitComponent: CourtHit,
  },
  opposingLiabilityInsurance: {
    entityApiData: {
      entityApiUri: "organizations",
      baseEntityAttribute: "opposingLiabilityInsurance",
      additionalSaveData: {
        type: "opposingLiabilityInsurance",
      },
      additionalBaseEntityData: {},
    },
    searchConfig: {
      indexName: ALGOLIA_ORGANIZATION_INDEX,
      requestOptions: { filters: "type:opposingLiabilityInsurance" },
      idPrefix: "organization",
    },
    resultListConfig: {
      itemSize: 150,
    },
    entityFormElements: opposingLiabilityInsuranceFields({
      path: "",
      translationPrefix: "opposingLiabilityInsurance",
    }),
    HitComponent: OrganizationHit,
  },
  opposingCarLiabilityInsurance: {
    entityApiData: {
      entityApiUri: "organizations",
      baseEntityAttribute: "opposingCarLiabilityInsurance",
      additionalSaveData: {
        type: "opposingCarLiabilityInsurance",
      },
      additionalBaseEntityData: {},
    },
    searchConfig: {
      indexName: ALGOLIA_ORGANIZATION_INDEX,
      requestOptions: { filters: "type:opposingCarLiabilityInsurance" },
      idPrefix: "organization",
    },
    resultListConfig: {
      itemSize: 150,
    },
    entityFormElements: opposingCarLiabilityInsuranceFields({
      path: "",
      translationPrefix: "opposingCarLiabilityInsurance",
      isDisabled: ({ currentUser }) =>
        !userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.developer, Roles.productManager]),
    }),
    HitComponent: OrganizationHit,
  },
};

export const getDatasetConfigFromProduct = (datasetType: string, product: any) => {
  let dataSetConfig = { ...datasetConfigs[datasetType] };
  _.set(dataSetConfig, "entityApiData.baseEntityApiUri", product["@id"]);
  if (_.includes(["settlement"], product.productClassName)) {
    _.set(dataSetConfig, "entityApiData.additionalBaseEntityData", {
      backofficeCaseId: product.backofficeCase.id,
      basePath: "extended",
    });
  }
  switch (datasetType) {
    case "contractualPartnerLegalEntity":
      _.set(
        dataSetConfig,
        "entityFormElements",
        contractualPartnerFields({
          path: product.productClassName + ".contractualPartnerLegalEntity.organization",
          hideAddressAddition: false,
        }),
      );
      break;
    case "debtCollectionAgency":
      _.set(
        dataSetConfig,
        "entityFormElements",
        debtCollectionAgencyFieldsReduced({
          path: product.productClassName + ".debtCollectionAgency.organization",
        }),
      );
      // TODO: Temporary adjustment, will be removed & refactored later
      _.set(
        dataSetConfig,
        "entityFormElementsModal",
        debtCollectionAgencyFields({
          path: product.productClassName + ".debtCollectionAgency.organization",
        }),
      );
      break;
    case "authority":
      _.set(
        dataSetConfig,
        "entityFormElements",
        authorityFields({
          path: product.productClassName + ".responsibleAuthority",
        }),
      );
      break;
    case "prosecution":
      _.set(
        dataSetConfig,
        "entityFormElements",
        prosecutionFields({
          path: product.productClassName + ".responsibleProsecution",
        }),
      );
      break;
    case "opposingLiabilityInsurance":
      _.set(
        dataSetConfig,
        "entityFormElements",
        opposingLiabilityInsuranceFields({
          path: product.productClassName + ".opposingLiabilityInsurance.organization",
          requireAddressValidation: true,
        }),
      );
      break;
    case "opposingCarLiabilityInsurance":
      _.set(
        dataSetConfig,
        "entityFormElements",
        opposingCarLiabilityInsuranceFields({
          path: product.productClassName + ".opposingCarLiabilityInsurance.organization",
          requireAddressValidation: true,
          isDisabled: ({ currentUser }) =>
            !userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.developer, Roles.productManager]),
        }),
      );

      break;
  }
  return dataSetConfig;
};

interface DuplicationConfig {
  entitySearchIndex: string;
  algoliaSearchOptions?: { filters: string };
  searchField: string;
  additionalFields: Array<string>;
  idPrefix: string;
}

const duplicationConfigs: { [x: string]: DuplicationConfig } = {
  contractualPartnerLegalEntity: {
    entitySearchIndex: ALGOLIA_ORGANIZATION_INDEX,
    algoliaSearchOptions: { filters: "type:contractualPartnerLegalEntity" },
    searchField: "legalName",
    additionalFields: ["name"],
    idPrefix: "organization",
  },
  debtCollectionAgency: {
    entitySearchIndex: ALGOLIA_ORGANIZATION_INDEX,
    algoliaSearchOptions: { filters: "type:debtCollectionAgency" },
    searchField: "name",
    additionalFields: [],
    idPrefix: "organization",
  },
  opposingLiabilityInsurance: {
    entitySearchIndex: ALGOLIA_ORGANIZATION_INDEX,
    algoliaSearchOptions: { filters: "type:opposingLiabilityInsurance" },
    searchField: "name",
    additionalFields: [],
    idPrefix: "organization",
  },
  responsibleAuthority: {
    entitySearchIndex: ALGOLIA_AUTHORITY_INDEX,
    searchField: "name",
    additionalFields: ["department", "subDepartment"],
    idPrefix: "authority",
  },
  responsibleProsecution: {
    entitySearchIndex: ALGOLIA_PROSECUTION_INDEX,
    searchField: "name",
    additionalFields: ["department", "subDepartment"],
    idPrefix: "prosecution",
  },
  responsibleCourt: {
    entitySearchIndex: ALGOLIA_COURT_INDEX,
    searchField: "name",
    additionalFields: [],
    idPrefix: "court",
  },
};

export interface DuplicationCheckResult {
  hasPartialMatch: boolean;
  hasFullMatch: boolean;
  searchField: string;
}

export async function duplicationChecker({
  values,
  type,
  currentFieldname = "",
  currentId = 0,
}: {
  values: {};
  type: string;
  currentFieldname: string;
  currentId?: number;
}): Promise<DuplicationCheckResult | null> {
  const duplicationConfig = duplicationConfigs[type];
  const fieldsToCheck = [...duplicationConfig.additionalFields, duplicationConfig.searchField];
  if (!fieldsToCheck.includes(currentFieldname)) {
    return null;
  }

  const searchClient = liteClient(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY);
  const searchValue = _.get(values, duplicationConfig.searchField);
  const { results } = await searchClient.searchForHits({
    requests: [
      {
        indexName: duplicationConfig.entitySearchIndex,
        query: searchValue,
        ...duplicationConfig.algoliaSearchOptions,
      },
    ],
  });
  const hits = results[0];
  const hasPartialMatch = (hits?.nbHits ?? 0) > 0;
  let hasFullMatch = false;
  for (const hit of hits?.hits ?? []) {
    hasFullMatch =
      //-- do skip the search entry for the current id!
      duplicationConfig.idPrefix + "::" + currentId !== hit.objectID &&
      //-- check other search entries for equality
      fieldsToCheck.reduce((aggregator: boolean, field: string) => {
        const valuesValue = _.get(values, field, "") || "";
        const fieldsValue = _.get(hit, field, "");
        return valuesValue == fieldsValue && aggregator;
      }, true);
    if (hasFullMatch) {
      break;
    }
  }

  return {
    hasPartialMatch,
    hasFullMatch,
    searchField: duplicationConfig.searchField,
  };
}
